// src/components/Messages/Messages.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom"; // ✅ ADDED
import {
  listInbox,
  markRead,
  sendDirectMessage,
  deleteMessage,
} from "../lib/messages";
import { getArticle } from "../lib/articles"; // ✅ ADDED
import supabase from "../supabase";

const Wrap = styled.div`
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;
const Title = styled.h2`
  margin: 0;
`;
const Tabs = styled.div`
  display: flex;
  gap: 8px;
`;
const Tab = styled.button`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  ${({ active }) =>
    active && `background:#111827;color:#fff;border-color:#111827;`}
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: ${({ unread }) => (unread ? "#f8fafc" : "#fff")};
`;
const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;
const Small = styled.span`
  font-size: 12px;
  color: #6b7280;
`;
const Subject = styled.div`
  font-weight: 700;
  margin: 4px 0;
`;
const Body = styled.div`
  white-space: pre-wrap;
`;
const ComposeWrap = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
`;
const Input = styled.input`
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;
const TextArea = styled.textarea`
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  min-height: 120px;
`;
const Btn = styled.button`
  padding: 8px 12px;
  border: 1px solid #111827;
  background: #111827;
  color: #fff;
  border-radius: 8px;
`;

const DeleteBtn = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  color: #ef4444;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 6px;
  &:hover {
    background: #fee2e2;
  }
`;

const ArticleLink = styled.button`
  background: none;
  border: none;
  color: #00876e;
  text-decoration: underline;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  margin-top: 8px;
  font-weight: 800;
  text-align: left;
  display: inline-block;
  &:hover {
    color: #0f766e;
  }
`;

export default function Messages({ profile, organization }) {
  const navigate = useNavigate(); // ✅ ADDED
  const orgId = profile?.company_id || organization?.id || null;
  const [tab, setTab] = useState("inbox"); // 'inbox' | 'compose'
  const [inbox, setInbox] = useState([]);
  const [members, setMembers] = useState([]);
  const [compose, setCompose] = useState({
    to_user: "",
    subject: "",
    body: "",
  });

  useEffect(() => {
    if (!orgId) return;

    (async () => {
      const items = await listInbox(orgId);
      setInbox(items);

      // load org members for compose dropdown
      const { data: orgUsers } = await supabase
        .from("Users")
        .select("id, name, email")
        .eq("company_id", orgId)
        .order("name");

      setMembers(orgUsers || []);
    })();
  }, [orgId]);

  const openMessage = async (m) => {
    if (m.status === "unread") {
      await markRead(m.id);
      setInbox((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, status: "read" } : x))
      );
    }
  };

  const onSend = async (e) => {
    e.preventDefault();
    await sendDirectMessage(compose, orgId);
    alert("Message sent.");
    setCompose({ to_user: "", subject: "", body: "" });
    setTab("inbox");
  };

  const onDelete = async (id) => {
    try {
      await deleteMessage(id);
      setInbox((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      console.error("Failed to delete message:", e);
      alert("Could not delete message.");
    }
  };

  const handleGoToArticle = async (e, articleId) => {
    e.stopPropagation(); // Prevent the card's onClick from firing twice
    try {
      // Fetch full article so the editor has all the context it needs
      const fullArticle = await getArticle(articleId);

      // Route directly to the content generation editor
      navigate(`/organization-portal/${orgId}/content-generation`, {
        state: { openArticle: fullArticle },
      });
    } catch (err) {
      console.error("Error opening article from message:", err);
      alert("Failed to load the article.");
    }
  };

  return (
    <Wrap>
      <Top>
        <Title>Messages</Title>
        <Tabs>
          <Tab active={tab === "inbox"} onClick={() => setTab("inbox")}>
            Inbox
          </Tab>
          <Tab active={tab === "compose"} onClick={() => setTab("compose")}>
            Compose
          </Tab>
        </Tabs>
      </Top>

      {tab === "inbox" && (
        <List>
          {inbox.map((m) => (
            <Card
              key={m.id}
              unread={m.status === "unread"}
              onClick={() => openMessage(m)}
            >
              <Row>
                <Small>{new Date(m.created_at).toLocaleString()}</Small>
                <Small>Type: {m.type}</Small>
                {m.article_id && <Small>Article</Small>}
                {m.project_id && <Small>Project</Small>}
                {m.status === "unread" && (
                  <Small style={{ color: "#ef4444" }}>UNREAD</Small>
                )}
                <DeleteBtn
                  title="Delete message"
                  onClick={(e) => {
                    e.stopPropagation(); // don't trigger openMessage
                    onDelete(m.id);
                  }}
                  aria-label="Delete message"
                >
                  🗑️
                </DeleteBtn>
              </Row>
              <Subject>{m.subject}</Subject>
              {m.body && <Body>{m.body}</Body>}
              {/* ✅ ADDED: Conditionally render the link if an article_id exists */}
              {m.article_id && (
                <div>
                  <ArticleLink
                    onClick={(e) => handleGoToArticle(e, m.article_id)}
                  >
                    → Open Article
                  </ArticleLink>
                </div>
              )}
            </Card>
          ))}
          {inbox.length === 0 && <Small>No messages yet.</Small>}
        </List>
      )}

      {tab === "compose" && (
        <ComposeWrap>
          <form onSubmit={onSend}>
            <Field>
              <label>To</label>
              <select
                value={compose.to_user}
                onChange={(e) =>
                  setCompose({ ...compose, to_user: e.target.value })
                }
              >
                <option value="">Select a member…</option>
                {members.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
            </Field>
            <Field>
              <label>Subject</label>
              <Input
                value={compose.subject}
                onChange={(e) =>
                  setCompose({ ...compose, subject: e.target.value })
                }
                required
              />
            </Field>
            <Field>
              <label>Message</label>
              <TextArea
                value={compose.body}
                onChange={(e) =>
                  setCompose({ ...compose, body: e.target.value })
                }
              />
            </Field>
            <Btn type="submit">Send</Btn>
          </form>
        </ComposeWrap>
      )}
    </Wrap>
  );
}
